<template>
  <!-- Problem statement confirmation -->
  <div v-if="problemStatementMessage" class="system-message">
    <strong>✓ Problem statement updated:</strong>
    {{ problemStatementMessage }}
  </div>
  
  <!-- New tool suggestions and steps display -->
  <ChatToolsDisplay v-if="hasTools" :toolSuggestions="toolSuggestions" />

  <!-- Message -->
  <div :class="['chat-message', message.role]">
    <div class="chat-bubble">
      <!-- User message: simple text -->
      <template v-if="message.role === 'user'">
        {{ message.content }}
      </template>

      <!-- Assistant message: text + tools if present -->
      <template v-else-if="message.role === 'assistant'">
        <div class="assistant-text" v-html="renderedContent"></div>
      </template>
    </div>
  </div>

  <ChatToolsDisplay v-if="hasTools" :nextSteps="nextSteps" />
</template>

<script>
import { marked } from "marked";
import DOMPurify from "dompurify";
import { ChatToolsDisplay } from "./chat/toolsDisplay/index.js";

export default {
  name: "ChatMessage",
  components: {
    ChatToolsDisplay,
  },
  props: {
    message: {
      type: Object,
      required: true,
      // {id, role, content, message_type, metadata, tools, created_at}
    },
  },
  computed: {
    problemStatementMessage() {
      if (!this.message.tools) return null;
      const problemTool = this.message.tools.find(
        (t) => t.tool_name === "save_problem_statement",
      );
      return problemTool?.tool_result || null;
    },
    renderedContent() {
      if (!this.message.content) return "";
      try {
        const html = marked(this.message.content);
        return DOMPurify.sanitize(html);
      } catch (e) {
        console.error("Error rendering markdown:", e);
        return DOMPurify.sanitize(this.message.content);
      }
    },
    toolSuggestions() {
      if (!this.message.tools) return [];
      const toolResult = this.message.tools.find(
        (t) => t.tool_name === "suggest_tools",
      );
      return toolResult?.tool_result || [];
    },
    nextSteps() {
      if (!this.message.tools) return [];
      const toolResult = this.message.tools.find(
        (t) => t.tool_name === "suggest_next_steps",
      );
      return toolResult?.tool_result || [];
    },
    hasTools() {
      return this.toolSuggestions.length > 0 || this.nextSteps.length > 0;
    },
  },
  methods: {
    handleToolSelect(tool) {
      this.$emit("tool-select", tool);
    },
    handlePromptSelect(prompt) {
      this.$emit("prompt-select", prompt);
    },
  },
};
</script>

<style scoped lang="scss">
.chat-message {
  display: flex;
  margin-bottom: 16px;
  animation: slideIn 0.3s ease-in-out;

  &.user {
    justify-content: flex-end;

    .chat-bubble {
      background-color: var(--primaryColor);
      color: var(--colorOnPrimary);
      border-radius: 12px 12px 4px 12px;
    }
  }

  &.assistant {
    justify-content: flex-start;

    .chat-bubble {
      background-color: var(--secondaryColorLighter);
      color: var(--darkColor);
      border-radius: 12px 12px 12px 4px;
    }
  }
}

.chat-bubble {
  max-width: 70%;
  padding: 14px 16px;
  word-wrap: break-word;
  line-height: 1.5;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.assistant-text {
  margin-bottom: 12px;
  overflow-wrap: break-word;

  &:last-child {
    margin-bottom: 0;
  }

  /* Markdown styling */
  :deep(h1),
  :deep(h2),
  :deep(h3),
  :deep(h4),
  :deep(h5),
  :deep(h6) {
    margin-top: 8px;
    margin-bottom: 6px;
    font-weight: 600;
  }

  :deep(h1) {
    font-size: 1.3rem;
  }

  :deep(h2) {
    font-size: 1.2rem;
  }

  :deep(h3) {
    font-size: 1.1rem;
  }

  :deep(p) {
    margin: 6px 0;
  }

  :deep(ul),
  :deep(ol) {
    margin: 6px 0 6px 20px;
    padding-left: 0;
  }

  :deep(li) {
    margin: 3px 0;
  }

  :deep(code) {
    background-color: rgba(0, 0, 0, 0.08);
    padding: 2px 6px;
    border-radius: 3px;
    font-family: monospace;
    font-size: 0.9em;
  }

  :deep(pre) {
    background-color: rgba(0, 0, 0, 0.08);
    padding: 8px 12px;
    border-radius: 4px;
    overflow-x: auto;
    margin: 6px 0;
  }

  :deep(blockquote) {
    border-left: 3px solid currentColor;
    padding-left: 10px;
    margin: 6px 0;
    opacity: 0.8;
  }

  :deep(a) {
    color: var(--primaryColor);
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}

.system-message {
  background-color: rgba(95, 151, 122, 0.08);
  border-left: 3px solid var(--primaryColor);
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 0.9rem;
  color: var(--darkColor);
  margin-bottom: 12px;
  font-family: system-ui, -apple-system, sans-serif;

  strong {
    color: var(--primaryColor);
    font-weight: 600;
  }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive: larger bubbles on mobile */
@media (max-width: 768px) {
  .chat-bubble {
    max-width: 85%;
  }
}
</style>
